import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, escrowAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DisputeCenter from '../components/escrow/DisputeCenter';
import {
  Scales,
  Warning,
  Clock,
  SpinnerGap,
  ArrowLeft,
  CheckCircle,
  XCircle,
  CurrencyDollar,
  PaperPlaneTilt,
} from '@phosphor-icons/react';

const PHASE_LABEL = {
  awaiting_response: 'Awaiting Other Party',
  awaiting_admin: 'Awaiting Admin',
  resolved: 'Resolved',
};

const PHASE_TONE = {
  awaiting_response: 'bg-amber-100 border-amber-400 text-amber-900',
  awaiting_admin: 'bg-blue-100 border-blue-400 text-blue-900',
  resolved: 'bg-emerald-100 border-emerald-400 text-emerald-900',
};

export default function AdminDisputes() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [overdue, setOverdue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(''); // '' = all active
  const [selected, setSelected] = useState(null); // escrow_id of selected dispute
  const [escrowDoc, setEscrowDoc] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveType, setResolveType] = useState('release_to_worker');
  const [splitPct, setSplitPct] = useState(50);
  const [adminNote, setAdminNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('userType') !== 'admin') {
      navigate('/login');
    }
  }, [navigate]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getDisputes(phase ? { phase } : {});
      setItems(r.data?.disputes || []);
      setOverdue(r.data?.overdue || 0);
    } catch (e) {
      toast({ title: 'Failed to load disputes', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [phase]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCase = async (id) => {
    setSelected(id);
    setEscrowDoc(null);
    try {
      const r = await escrowAPI.getById(id);
      if (r.data?.success) setEscrowDoc(r.data.escrow);
    } catch (e) {
      toast({ title: 'Failed to load escrow', variant: 'destructive' });
    }
  };

  const submitResolution = async () => {
    setSubmitting(true);
    try {
      await adminAPI.resolveDispute(selected, {
        resolution_type: resolveType,
        split_percentage: splitPct,
        note: adminNote,
      });
      toast({ title: 'Dispute resolved', description: 'Both parties have been notified.' });
      setResolveOpen(false);
      setAdminNote('');
      setSelected(null);
      setEscrowDoc(null);
      await fetchList();
    } catch (e) {
      toast({ title: 'Failed to resolve', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const held = Number(escrowDoc?.held_amount || escrowDoc?.total_amount || 0);
  const workerAmt = resolveType === 'release_to_worker' ? held
    : resolveType === 'refund_to_contractor' ? 0
    : Math.round(held * (Number(splitPct) / 100) * 100) / 100;
  const contractorRefund = held - workerAmt;

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="admin-disputes-page">
      {/* Header */}
      <div className="bg-neutral-900 text-white py-6 border-b-4 border-black">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="text-white/80 hover:text-white" data-testid="back-to-admin-btn">
              <ArrowLeft size={26} weight="bold" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
                <Scales size={28} weight="fill" className="text-amber-400" />
                Disputes Queue
              </h1>
              <p className="text-neutral-300 text-sm mt-1">
                Triage escrow disputes by SLA aging. {overdue > 0 && (
                  <span className="text-red-400 font-bold ml-1">{overdue} overdue</span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5 space-y-3">
          <div className="flex gap-2 mb-3 flex-wrap">
            {[
              { v: '', label: 'All Active' },
              { v: 'awaiting_admin', label: 'Awaiting Admin' },
              { v: 'awaiting_response', label: 'Awaiting Other Party' },
              { v: 'resolved', label: 'Resolved' },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setPhase(f.v)}
                className={`px-3 h-9 text-xs font-bold rounded-lg border-2 ${phase === f.v ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-black'}`}
                data-testid={`filter-${f.v || 'all'}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center gap-3 p-6 bg-white border-2 border-black rounded-xl text-neutral-600">
              <SpinnerGap size={22} className="animate-spin" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-6 bg-white border-2 border-dashed border-neutral-300 rounded-xl text-neutral-500 text-center" data-testid="empty-disputes">
              <CheckCircle size={36} weight="light" className="mx-auto mb-2 text-emerald-500" />
              No disputes in this view.
            </div>
          ) : (
            items.map((d) => (
              <button
                key={d.escrow_id}
                onClick={() => openCase(d.escrow_id)}
                className={`w-full text-left bg-white border-2 rounded-xl p-4 shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] hover:-translate-y-0.5 transition-all ${selected === d.escrow_id ? 'border-[#2B4C6F]' : 'border-black'}`}
                data-testid={`dispute-row-${d.escrow_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-neutral-900 truncate">{d.job_title}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {d.contractor_name} ↔ {d.worker_name}
                    </p>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded border-2 flex-shrink-0 ${PHASE_TONE[d.dispute_phase] || 'bg-neutral-100 border-neutral-300 text-neutral-700'}`}>
                    {PHASE_LABEL[d.dispute_phase] || d.dispute_phase}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-neutral-600 flex items-center gap-1">
                    <CurrencyDollar size={14} weight="bold" />
                    <span className="font-bold">${(d.held_amount || d.total_amount || 0).toLocaleString()}</span>
                    <span className="text-neutral-400">held</span>
                  </span>
                  <span className={`flex items-center gap-1 font-bold ${d.is_overdue ? 'text-red-600' : 'text-neutral-500'}`}>
                    {d.is_overdue ? <Warning size={14} weight="fill" /> : <Clock size={14} weight="bold" />}
                    {d.sla_hours != null ? `${d.sla_hours}h` : '—'}
                  </span>
                </div>
                {d.reason && (
                  <p className="text-xs text-neutral-500 mt-2 italic truncate">"{d.reason.replace(/_/g, ' ')}"</p>
                )}
              </button>
            ))
          )}
        </div>

        {/* Case detail */}
        <div className="lg:col-span-7">
          {!selected ? (
            <div className="bg-white border-2 border-dashed border-neutral-300 rounded-xl p-8 text-center text-neutral-500" data-testid="no-selection-state">
              <Scales size={48} weight="light" className="mx-auto mb-3 text-neutral-300" />
              <p className="font-bold">Select a dispute to review</p>
              <p className="text-xs mt-1">Pick a case from the list to see both sides' statements and evidence.</p>
            </div>
          ) : !escrowDoc ? (
            <div className="bg-white border-2 border-black rounded-xl p-6 flex items-center gap-3 text-neutral-600">
              <SpinnerGap size={22} className="animate-spin" />
              Loading case file…
            </div>
          ) : (
            <>
              <DisputeCenter escrow={escrowDoc} currentUserId={'__admin__'} onUpdate={() => openCase(selected)} />

              {escrowDoc.status === 'disputed' && (
                <div className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] p-6" data-testid="admin-resolve-panel">
                  <h3 className="text-lg font-black text-neutral-900 mb-4 flex items-center gap-2">
                    <Scales size={20} weight="fill" className="text-amber-500" />
                    Issue Resolution
                  </h3>
                  {!resolveOpen ? (
                    <button
                      onClick={() => setResolveOpen(true)}
                      className="w-full h-12 flex items-center justify-center gap-2 bg-amber-500 text-black font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                      data-testid="open-resolve-btn"
                    >
                      <Scales size={20} weight="bold" />
                      Resolve Dispute
                    </button>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label className="font-bold mb-2 block">Resolution Type</Label>
                        <div className="grid grid-cols-1 gap-2">
                          {[
                            { v: 'release_to_worker', label: 'Release all funds to worker', icon: <CheckCircle size={18} weight="fill" className="text-emerald-600" /> },
                            { v: 'refund_to_contractor', label: 'Refund all funds to contractor', icon: <XCircle size={18} weight="fill" className="text-red-600" /> },
                            { v: 'split', label: 'Split funds (custom %)', icon: <Scales size={18} weight="fill" className="text-amber-600" /> },
                          ].map((opt) => (
                            <label
                              key={opt.v}
                              className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${resolveType === opt.v ? 'border-black bg-amber-50' : 'border-neutral-200 hover:border-neutral-400'}`}
                              data-testid={`resolve-option-${opt.v}`}
                            >
                              <input
                                type="radio"
                                name="resolveType"
                                value={opt.v}
                                checked={resolveType === opt.v}
                                onChange={(e) => setResolveType(e.target.value)}
                                className="accent-black"
                              />
                              {opt.icon}
                              <span className="font-bold text-sm">{opt.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {resolveType === 'split' && (
                        <div data-testid="split-controls">
                          <Label className="font-bold mb-2 block">Worker share: {splitPct}%</Label>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            step={5}
                            value={splitPct}
                            onChange={(e) => setSplitPct(Number(e.target.value))}
                            className="w-full accent-amber-500"
                            data-testid="split-slider"
                          />
                          <div className="flex justify-between text-xs text-neutral-500 mt-1">
                            <span>0%</span><span>50%</span><span>100%</span>
                          </div>
                        </div>
                      )}

                      <div className="bg-neutral-50 border-2 border-neutral-200 rounded-lg p-3 text-sm">
                        <div className="flex justify-between mb-1">
                          <span className="text-neutral-600">Held in escrow:</span>
                          <span className="font-black">${held.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between mb-1 text-emerald-700">
                          <span className="font-bold">→ Worker payout:</span>
                          <span className="font-black">${workerAmt.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-red-700">
                          <span className="font-bold">→ Contractor refund:</span>
                          <span className="font-black">${contractorRefund.toLocaleString()}</span>
                        </div>
                      </div>

                      <div>
                        <Label className="font-bold mb-2 block">Admin note (visible to both parties)</Label>
                        <Textarea
                          value={adminNote}
                          onChange={(e) => setAdminNote(e.target.value)}
                          rows={3}
                          placeholder="Explain the basis for this decision…"
                          className="border-2 border-black rounded-xl"
                          data-testid="admin-note-input"
                        />
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => setResolveOpen(false)}
                          className="flex-1 h-12 bg-neutral-100 text-neutral-700 font-bold rounded-xl border-2 border-neutral-300"
                          data-testid="cancel-resolve-btn"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={submitResolution}
                          disabled={submitting}
                          className="flex-1 h-12 flex items-center justify-center gap-2 bg-amber-500 text-black font-bold rounded-xl border-2 border-black disabled:opacity-50"
                          data-testid="confirm-resolve-btn"
                        >
                          {submitting ? <SpinnerGap size={20} className="animate-spin" /> : <><PaperPlaneTilt size={18} weight="bold" /> Confirm Resolution</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
